import React from 'react';
import { t } from '@lingui/macro';
import {
    Button,
    ButtonProps,
    IconButton,
    AddToCollectionOutlined,
    PopoverPlacement,
} from '@landr/maestro';
import { noop } from 'utils/noop';
import { useOpenAddToCollectionPrompt } from 'actions/UserCollection';
import { AnalyticsSource } from 'types';
import useAddSampleToCollectionButton from 'components/CommonUi/AddSampleToCollectionButton/useAddSampleToCollectionButton';
import { Sample } from 'types/generated/graphql';

export interface AddSampleToCollectionButtonProps extends Partial<ButtonProps> {
    samples: Sample[];
    source: AnalyticsSource;
    selectedSamples?: string[];
    onClick?: () => void;
    hide?: () => void;
    setPopover?: (child: React.ReactElement | undefined) => void;
    placeholder?: string;
    parentRef?: any;
    popoverPlacement?: PopoverPlacement;
}

const AddSampleToCollectionButton: React.FC<
    AddSampleToCollectionButtonProps
> = ({
    samples,
    source,
    onClick = noop,
    hide = noop,
    setPopover,
    popoverPlacement,
    placeholder,
    children,
    ...props
}) => {
    const { AddToCollection, anchorRef, openAddToCollection, CollectionsList } =
        useOpenAddToCollectionPrompt();

    const setCollectionListToPopover = () => {
        setPopover &&
            setPopover(
                <CollectionsList
                    hide={hide}
                    samples={samples}
                    placeholder={placeholder}
                />,
            );
    };
    const { handleOnClick } = useAddSampleToCollectionButton({
        onClick,
        samples,
        setCollectionListToPopover,
        source,
        placeholder,
        hide,
        openAddToCollection,
    });

    return children ? (
        <>
            <Button
                ref={anchorRef}
                onClick={handleOnClick}
                icon={AddToCollectionOutlined}
                data-testid="add-to-collection-btn"
                aria-label={t`Add to my collection`}
                {...props}
            >
                {children}
            </Button>
            {!setPopover && (
                <AddToCollection
                    samples={samples}
                    anchorRef={anchorRef}
                    placement={popoverPlacement}
                />
            )}
        </>
    ) : (
        <>
            <IconButton
                ref={anchorRef}
                onClick={handleOnClick}
                icon={AddToCollectionOutlined}
                size="md"
                kind={props.kind}
                variant={props.variant}
                data-testid="add-to-collection-btn"
                aria-label={t`Add to my collection`}
                label={t`Add to my collection`}
                {...props}
            />
            {!setPopover && (
                <AddToCollection
                    samples={samples}
                    anchorRef={anchorRef}
                    placement={popoverPlacement}
                />
            )}
        </>
    );
};

export default AddSampleToCollectionButton;
